import Button, { ButtonTypes } from "atoms/buttons/button";
import ModalCloseButton from "atoms/buttons/modal-close-button";
import { ButtonStyles } from "atoms/constants/button-styles";
import { HeadingPriority } from "atoms/constants/heading-priority";
import Heading from "atoms/typography/heading";
import Icon from "atoms/icons/icon";
import { IconSizes } from "atoms/constants/icon-sizes";
import { Icons } from "atoms/constants/icons";
import ConversationRecord from "models/view-models/conversations/conversation-record";
import { ModalCloseButtonStyle } from "molecules/constants/modal-close-button-style";
import { ModalTypes } from "molecules/constants/modal-types";
import { ModalTransitions } from "molecules/constants/modal-transitions";
import InputFormField from "molecules/form-fields/input-form-field";
import Form from "molecules/forms/form";
import Modal, { ModalProps } from "molecules/modals/modal";
import React, { useEffect, useRef, useState } from "react";
import { useLocalization } from "utilities/hooks/use-localization";
import StringUtils from "utilities/string-utils";

// -----------------------------------------------------------------------------------------
// #region Interfaces
// -----------------------------------------------------------------------------------------

interface ChatRenameConversationModalProps
    extends Pick<ModalProps, "isVisible" | "closeDialog"> {
    conversation: Pick<ConversationRecord, "id" | "title">;
    onSave: (conversation: Pick<ConversationRecord, "id" | "title">) => void;
}

// #endregion Interfaces

// -----------------------------------------------------------------------------------------
// #region Constants
// -----------------------------------------------------------------------------------------

const CSS_CLASS_NAME = "c-chat-rename-conversation-modal";

// #endregion Constants

// -----------------------------------------------------------------------------------------
// #region Component
// -----------------------------------------------------------------------------------------

const ChatRenameConversationModal: React.FC<
    ChatRenameConversationModalProps
> = (props) => {
    const { closeDialog, isVisible, conversation, onSave } = props;
    const { t } = useLocalization();
    const [conversationTitle, setConversationTitle] = useState<string>(
        conversation.title ?? ""
    );
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (isVisible) {
            setConversationTitle(conversation.title ?? "");
            setTimeout(() => {
                if (inputRef.current) {
                    inputRef.current.focus();
                    inputRef.current.select();
                }
            }, 100);
        }
    }, [isVisible, conversation.title]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setConversationTitle(e.currentTarget.value);
    };

    const handleClose = () => {
        setConversationTitle(conversation.title ?? "");
        closeDialog();
    };

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        handleSave();
    };

    const handleSave = () => {
        const trimmedTitle = conversationTitle.trim();
        if (StringUtils.hasValue(trimmedTitle)) {
            onSave({
                id: conversation.id,
                title: trimmedTitle,
            });
            handleClose();
        }
    };

    const isValid = StringUtils.hasValue(conversationTitle.trim());

    return (
        <Modal
            closeButtonStyle={ModalCloseButtonStyle.InsideDialog}
            closeDialog={handleClose}
            cssClassName={CSS_CLASS_NAME}
            isVisible={isVisible}
            label={t("rename")}
            transition={ModalTransitions.SlideRight}
            type={ModalTypes.Base}>
            <div className={`${CSS_CLASS_NAME}__header`}>
                <Heading priority={HeadingPriority.Two}>
                    {t("rename-conversation")}
                </Heading>
                <ModalCloseButton onClick={handleClose} />
            </div>
            <div className={`${CSS_CLASS_NAME}__body`}>
                <div className={`${CSS_CLASS_NAME}__content`}>
                    <Form onSubmit={handleSubmit}>
                        <InputFormField
                            autofocus
                            isValid={true}
                            label={t("conversation-name")}
                            onChange={handleChange}
                            placeholder={t("rename-conversation")}
                            ref={inputRef}
                            value={conversationTitle}
                        />
                    </Form>
                </div>
                <div className={`${CSS_CLASS_NAME}__footer`}>
                    <Button
                        onClick={handleClose}
                        style={ButtonStyles.Secondary}>
                        <Icon type={Icons.Close} size={IconSizes.Medium}></Icon>
                        {t("cancel")}
                    </Button>
                    <Button
                        onClick={handleSave}
                        disabled={!isValid}
                        type={ButtonTypes.Submit}
                        style={ButtonStyles.Primary}>
                        <Icon
                            type={Icons.Checkmark}
                            size={IconSizes.Medium}></Icon>
                        {t("save")}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

// #endregion Component

// -----------------------------------------------------------------------------------------
// #region Exports
// -----------------------------------------------------------------------------------------

export default ChatRenameConversationModal;

// #endregion Exports
